import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Loader2,
  Check,
  Circle,
  CircleDot,
  AlertTriangle,
  Send,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  getDSREntryReport,
  getDailySetup,
  getPersonnelList,
  getLitePersonnel,
  getActivitiesList,
  getEquipmentList,
  getPermitsList,
  getMeetingsList,
  getJSAEvaluationsList,
  getSWAEventsList,
  getSafetyEventsList,
  getHSEObservationsList,
  getForecastedActivitiesList,
  submitDSR,
  type DSRVariant,
} from '@/services/db/dsrEntry';
import { useAuthStore } from '@/store/authStore';
import DailySetupForm from '@/components/dsr/entry/DailySetupForm';
import WorkforceForm from '@/components/dsr/entry/WorkforceForm';
import PersonnelLiteForm from '@/components/dsr/entry/PersonnelLiteForm';
import ActivitiesForm from '@/components/dsr/entry/ActivitiesForm';
import EquipmentForm from '@/components/dsr/entry/EquipmentForm';
import PermitsForm from '@/components/dsr/entry/PermitsForm';
import SafetyMeetingsForm from '@/components/dsr/entry/SafetyMeetingsForm';
import JSAEvaluationsForm from '@/components/dsr/entry/JSAEvaluationsForm';
import SWAForm from '@/components/dsr/entry/SWAForm';
import SafetyEventsForm from '@/components/dsr/entry/SafetyEventsForm';
import GoodCatchForm from '@/components/dsr/entry/GoodCatchForm';
import ForecastingForm from '@/components/dsr/entry/ForecastingForm';
import ShiftLogForm from '@/components/dsr/entry/ShiftLogForm';
import ActionItemsForm from '@/components/dsr/entry/ActionItemsForm';
import RiskAssessmentForm from '@/components/dsr/entry/RiskAssessmentForm';
import ShiftBriefCard from '@/components/dsr/ShiftBriefCard';
import { getShiftLogEntries } from '@/services/db/shiftLog';
import { getActionItems } from '@/services/db/actionItems';

type SectionId =
  | 'daily_setup'
  | 'personnel'
  | 'personnel_lite'
  | 'activities'
  | 'equipment'
  | 'permits'
  | 'shift_brief'
  | 'safety_meetings'
  | 'jsa_evaluations'
  | 'swa'
  | 'safety_events'
  | 'hse_observations'
  | 'forecasting'
  | 'narrative'
  | 'action_items'
  | 'risk_assessment';

interface SectionMeta {
  id: SectionId;
  label: string;
  built: boolean;
}

const SECTIONS: SectionMeta[] = [
  { id: 'daily_setup', label: 'Daily Setup', built: true },
  { id: 'personnel', label: 'Workforce', built: true },
  { id: 'personnel_lite', label: 'Personnel On Board', built: true },
  { id: 'activities', label: 'Activities', built: true },
  { id: 'permits', label: 'Permits', built: true },
  { id: 'shift_brief', label: 'Shift Brief', built: true },
  { id: 'safety_meetings', label: 'Safety Meetings', built: true },
  { id: 'jsa_evaluations', label: 'JSA Evaluations', built: true },
  { id: 'swa', label: 'Stop Work Authority', built: true },
  { id: 'safety_events', label: 'Safety Events', built: true },
  { id: 'hse_observations', label: 'Good Catch', built: true },
  { id: 'narrative', label: 'Shift Log', built: true },
  { id: 'forecasting', label: 'Forecasting', built: true },
  { id: 'action_items', label: 'Action Items', built: true },
  { id: 'risk_assessment', label: 'Risk Assessment', built: true },
];

// Variant-aware sidebar composition. daily_setup is always present.
// Standard and Full are intentionally identical today — Full reserves the
// post-pilot Lessons Learned Library section as the future-only addition
// (ARCH-A1 / ARCH-A3).
const VARIANT_SIDEBAR_IDS: Record<DSRVariant, ReadonlySet<SectionId>> = {
  lite: new Set<SectionId>(['daily_setup', 'personnel_lite', 'narrative']),
  standard: new Set<SectionId>([
    'daily_setup',
    'personnel',
    'activities',
    'permits',
    'shift_brief',
    'safety_meetings',
    'jsa_evaluations',
    'swa',
    'safety_events',
    'hse_observations',
    'narrative',
    'forecasting',
    'action_items',
    'risk_assessment',
  ]),
  full: new Set<SectionId>([
    'daily_setup',
    'personnel',
    'activities',
    'permits',
    'shift_brief',
    'safety_meetings',
    'jsa_evaluations',
    'swa',
    'safety_events',
    'hse_observations',
    'narrative',
    'forecasting',
    'action_items',
    'risk_assessment',
  ]),
};

export default function EditDSRPage() {
  const { dsrId } = useParams<{ dsrId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const [activeSection, setActiveSection] = useState<SectionId>('daily_setup');
  const [submitting, setSubmitting] = useState(false);

  const { data: report, isLoading: loadingReport, isError: reportError } = useQuery({
    queryKey: ['entry_dsr_header', dsrId],
    queryFn: () => getDSREntryReport(dsrId!),
    enabled: !!dsrId,
  });

  const completion = useQuery({
    queryKey: ['entry_completion', dsrId],
    queryFn: async () => {
      const [
        daily,
        personnel,
        litePersonnel,
        activities,
        equipment,
        permits,
        meetings,
        jsa,
        swa,
        safetyEvents,
        observations,
        forecasted,
        shiftLogs,
        actionItems,
      ] = await Promise.all([
        getDailySetup(dsrId!),
        getPersonnelList(dsrId!),
        getLitePersonnel(dsrId!),
        getActivitiesList(dsrId!),
        getEquipmentList(dsrId!),
        getPermitsList(dsrId!),
        getMeetingsList(dsrId!),
        getJSAEvaluationsList(dsrId!),
        getSWAEventsList(dsrId!),
        getSafetyEventsList(dsrId!),
        getHSEObservationsList(dsrId!),
        getForecastedActivitiesList(dsrId!),
        getShiftLogEntries(dsrId!).catch(() => []),
        getActionItems(dsrId!).catch(() => []),
      ]);
      return {
        daily_setup: daily != null,
        personnel: personnel.length > 0,
        // Lite Personnel completion: both POB and SSE must be set. Crew
        // note is optional. The DB CHECK constraints enforce SSE ≤ POB
        // and non-negativity; we only check presence here.
        personnel_lite:
          litePersonnel?.lite_total_pob != null && litePersonnel?.lite_sse_count != null,
        activities: activities.length > 0,
        equipment: equipment.length > 0,
        permits: permits.length > 0,
        safety_meetings: meetings.length > 0,
        jsa_evaluations: jsa.length > 0,
        swa: swa.length > 0,
        safety_events: safetyEvents.length > 0,
        hse_observations: observations.length > 0,
        forecasting: forecasted.length > 0,
        narrative: shiftLogs.length > 0,
        action_items: actionItems.length > 0,
        // Risk assessment is optional — not a completion gate.
        risk_assessment: true,
      } as Record<SectionId, boolean>;
    },
    enabled: !!dsrId,
    staleTime: 30 * 1000,
  });

  const isReadOnly = useMemo(() => {
    if (!report) return true;
    if (report.status === 'draft') return report.created_by !== userId;
    return true;
  }, [report, userId]);

  // Variant drives which sections appear in the sidebar. Default 'standard'
  // is just for the brief loading window before report resolves.
  const variant: DSRVariant = (report?.variant ?? 'standard') as DSRVariant;
  const isLite = variant === 'lite';
  const visibleSections = useMemo(
    () => SECTIONS.filter((s) => VARIANT_SIDEBAR_IDS[variant].has(s.id)),
    [variant],
  );

  // If the active section isn't part of the loaded DSR's variant, snap back
  // to daily_setup. Covers e.g. opening a Lite DSR after navigating away
  // from a Standard one with activeSection='permits'.
  useEffect(() => {
    if (!report) return;
    if (!VARIANT_SIDEBAR_IDS[variant].has(activeSection)) {
      setActiveSection('daily_setup');
    }
  }, [variant, activeSection, report]);

  if (loadingReport) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  if (reportError || !report) {
    return (
      <div className="mx-auto max-w-xl py-10">
        <Card>
          <CardContent className="flex items-start gap-3 py-6">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-mozaic-orange" />
            <div className="space-y-1">
              <p className="font-medium">Could not load this report</p>
              <p className="text-sm text-muted-foreground">
                You may not have access, or the report no longer exists.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => navigate('/dashboard')}
              >
                Back to dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const projectName = report.projects?.name ?? 'Project';
  const projectCode = report.projects?.code ? ` (${report.projects.code})` : '';
  const dailySetupComplete = completion.data?.daily_setup ?? false;

  // Project metadata context strip — only show what is populated.
  const operator = report.projects?.operator_name?.trim();
  const rig = report.projects?.rig_name?.trim();
  const well = report.projects?.well_name?.trim();
  const phase = report.projects?.operational_phase?.trim();
  const contextParts: { label: string; value: string }[] = [];
  if (operator) contextParts.push({ label: 'Operator', value: operator });
  if (rig) contextParts.push({ label: 'Rig', value: rig });
  if (well) contextParts.push({ label: 'Well', value: well });
  if (phase) contextParts.push({ label: 'Phase', value: phase });

  const onSaveAndExit = () => navigate('/dashboard');

  const onSubmit = async () => {
    if (!dailySetupComplete) {
      alert('Daily Setup must be completed before submitting.');
      return;
    }
    if (!confirm('Submit this report? It will be visible to your supervisor.')) {
      return;
    }
    setSubmitting(true);
    try {
      await submitDSR(report.id);
      queryClient.invalidateQueries({ queryKey: ['ssr_my_today_dsr'] });
      queryClient.invalidateQueries({ queryKey: ['ssr_project_dsr_feed'] });
      navigate(`/reports/${report.id}`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onSaveAndExit}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Save & Exit
        </Button>
        <div className="text-xs text-muted-foreground">
          {report.status === 'draft' ? 'Draft — auto-saving' : `Status: ${report.status}`}
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* ── Section nav ── */}
        <aside className="w-full lg:sticky lg:top-4 lg:h-fit lg:w-[220px] lg:shrink-0">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <p className="text-sm font-semibold text-mozaic-navy">
                  {projectName}
                  {projectCode}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {format(new Date(report.report_date), 'EEE, MMM d, yyyy')}
                  {' · '}
                  <span className="capitalize">{report.shift_type}</span> shift
                </p>
                {contextParts.length > 0 && (
                  <div className="mt-2 space-y-0.5 border-t pt-2">
                    {contextParts.map((p) => (
                      <p key={p.label} className="text-[11px] text-muted-foreground">
                        <span className="font-semibold uppercase tracking-wide">{p.label}:</span>{' '}
                        <span className="text-foreground">{p.value}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {isLite && !isReadOnly && (
                <div className="rounded-md border border-mozaic-teal bg-mozaic-teal/10 p-2">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-mozaic-teal">
                    Lite Daily Report
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Streamlined for this project — Personnel On Board and Shift Log only.
                  </p>
                </div>
              )}

              <nav className="space-y-1">
                {visibleSections.map((section) => {
                  const isActive = activeSection === section.id;
                  const isComplete = completion.data?.[section.id] ?? false;
                  return (
                    <button
                      type="button"
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                        isActive
                          ? 'bg-mozaic-teal/15 font-semibold text-mozaic-teal'
                          : 'hover:bg-muted'
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-4 w-4 shrink-0 text-mozaic-green" />
                      ) : isActive ? (
                        <CircleDot className="h-4 w-4 shrink-0 text-mozaic-teal" />
                      ) : (
                        <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate">{section.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="border-t pt-3">
                <Button
                  className="w-full bg-mozaic-teal text-white hover:bg-mozaic-teal/90 disabled:opacity-50"
                  onClick={onSubmit}
                  disabled={!dailySetupComplete || submitting || isReadOnly}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="mr-1 h-4 w-4" />
                      Submit Report
                    </>
                  )}
                </Button>
                {!dailySetupComplete && (
                  <p className="mt-2 text-[10px] text-muted-foreground">
                    Complete Daily Setup first.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* ── Active form ── */}
        <main className="min-w-0 flex-1">
          {activeSection === 'daily_setup' && (
            <DailySetupForm
              dsrId={report.id}
              initialShiftType={report.shift_type}
              isReadOnly={isReadOnly}
            />
          )}
          {activeSection === 'personnel' && (
            <WorkforceForm
              dsrId={report.id}
              isReadOnly={isReadOnly}
              projectId={report.project_id}
              projoProjectId={report.projects?.projo_project_id ?? null}
              reportDate={report.report_date}
            />
          )}
          {activeSection === 'personnel_lite' && (
            <PersonnelLiteForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'activities' && (
            <ActivitiesForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'equipment' && (
            <EquipmentForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'permits' && (
            <PermitsForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'shift_brief' && (
            <ShiftBriefCard
              dsrId={report.id}
              tenantId={report.tenant_id}
              reportStatus={report.status}
              mode="entry"
            />
          )}
          {activeSection === 'safety_meetings' && (
            <SafetyMeetingsForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'jsa_evaluations' && (
            <JSAEvaluationsForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'swa' && <SWAForm dsrId={report.id} isReadOnly={isReadOnly} />}
          {activeSection === 'safety_events' && (
            <SafetyEventsForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'hse_observations' && (
            <GoodCatchForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'forecasting' && (
            <ForecastingForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'narrative' && (
            <ShiftLogForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'action_items' && (
            <ActionItemsForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'risk_assessment' && (
            <RiskAssessmentForm dsrId={report.id} isReadOnly={isReadOnly} />
          )}
        </main>
      </div>
    </div>
  );
}
